/**
 * Astromeda — 固定フッター（合計金額・分割払い・CTA）
 *
 * 商品ページとカート画面の下部に固定表示する。Shadow DOM でテーマCSSと隔離する。
 *
 * 設定は snippets/astro-sticky-footer.liquid が window.AstroStickyFooterConfig に流し込む。
 * 分割払いの月額は実質年率(APR)のアモチゼーション式で求める。
 *   月利 r = 年率 / 12
 *   月額 M = 元金 x r / (1 - (1+r)^-回数)
 */
(function () {
  "use strict";

  var DEFAULT_CONFIG = {
    mode: "auto",
    default_count: 36,
    scroll_threshold_pct: 10,
    installment_counts: [3, 6, 12, 24, 36, 48, 60],
    // fees の値は「実質年率」(APR) として解釈される (decimal、0.125 = 年率 12.5%)。
    // 月額 = 元金 × (年率/12) / (1 - (1 + 年率/12)^-回数) の amortization 公式。
    // 回数が増えるほど支払総額・手数料が膨らむ自然な挙動になる。
    fees: { "3": 0.125, "6": 0.125, "12": 0.125, "24": 0.125, "36": 0.125, "42": 0.125, "48": 0.125, "54": 0.125, "60": 0.125 },
    cta_text: null,
    cta_action: "auto",
    note_text: "※実際の手数料率はご利用のカード会社により異なります",
    desktop_visible: false,
    // 同居ウィジェットを隠すための CSS セレクタ (適合すれば即非表示)
    hide_selectors: [],
    // テキスト一致で隠す: 指定文字を含む要素を見つけ、その fixed/absolute な
    // 祖先 (ボタンや wrapper) を非表示に。チャット系ボタンに有効。
    hide_texts: ["TikTokはこちら", "ご相談はこちら"]
  };

  function fetchJson(url) {
    return fetch(url, { credentials: "same-origin", headers: { Accept: "application/json" } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }
  function formatJpy(n) { return "¥" + Math.round(n).toLocaleString("ja-JP"); }
  // 実質年率 (APR) ベースで月額を算出。fees の値は年率 (decimal) として解釈。
  //   月利 r = annualRate / 12
  //   月額 M = P × r / (1 - (1+r)^-n)
  //   (r=0 のときは P/n)
  // これにより回数が増えるほど支払総額が膨らむ (自然な挙動)。
  function calcMonthlyByApr(principal, count, annualRate) {
    if (principal <= 0 || count <= 0) return 0;
    if (!annualRate || annualRate <= 0) return Math.ceil(principal / count);
    var r = annualRate / 12;
    var pow = Math.pow(1 + r, -count);
    return Math.ceil(principal * r / (1 - pow));
  }
  function isMobile() {
    var ua = navigator.userAgent || "";
    if (/iPad|Tablet|PlayBook|Silk/.test(ua) || (/Android/.test(ua) && !/Mobile/.test(ua))) return false;
    return /Mobi|Android|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua) || window.innerWidth < 768;
  }
  function getQueryParam(name) {
    try {
      var url = new URL(location.href);
      return url.searchParams.get(name);
    } catch (e) { return null; }
  }
  function extractProductHandle() {
    var m = location.pathname.match(/\/products\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }
  function detectMode(configMode) {
    if (configMode && configMode !== "auto") return configMode;
    if (location.pathname.indexOf("/products/") !== -1) return "pdp";
    if (location.pathname.indexOf("/cart") !== -1) return "cart";
    return "pdp"; // unknown URL は何もしない (apply() で URL チェック)
  }

  function buildShadow(config, defaultCtaText, brandColor, ctaRadius) {
    brandColor = brandColor || "#e13124";
    ctaRadius = ctaRadius != null ? ctaRadius : 10;
    var host = document.createElement("div");
    host.id = "mdl-sticky-footer-host";
    // z-index: int 最大値 (2^31 - 1)。チャット系 widget (LINE/TikTok 連絡ボタン等)
    // より確実に前面に。isolation で新しい stacking context も明示作成。
    // translate3d/will-change で GPU レイヤーを強制 → Chrome スクロール中の浮き対策。
    host.style.cssText =
      "all:initial;position:fixed;left:0;right:0;bottom:0;" +
      "z-index:2147483647;pointer-events:none;isolation:isolate;" +
      "transform:translate3d(0,0,0);-webkit-transform:translate3d(0,0,0);" +
      "will-change:transform;backface-visibility:hidden;-webkit-backface-visibility:hidden;";
    document.body.appendChild(host);
    var shadow = host.attachShadow({ mode: "open" });
    // 完全カスタムのドロップダウン。<select> を使わないことで:
    //  - iOS の native picker (画面下から出るやつ) を回避
    //  - dropdown のフォントサイズ・位置・スタイルを完全制御
    //  - z-index も親 Shadow DOM 内なので host の最大 z-index に乗る
    shadow.innerHTML =
      "<style>" +
      ":host{all:initial;}" +
      // 余白タイト化: top 8 / 横 14 / 下 8 (iPhone は safe-area-inset で home bar 分だけ確保)
      // フッター自体は控えめにフェードのみ。注目誘導は CTA のパルスで行う。
      ".wrap{font-family:-apple-system,'Hiragino Kaku Gothic ProN','Noto Sans JP',sans-serif;color:#111;background:#fff;border-top:1px solid #e5e7eb;box-shadow:0 -8px 24px rgba(0,0,0,0.12);padding:8px 14px max(2px, env(safe-area-inset-bottom, 2px));pointer-events:auto;opacity:0;transition:opacity .15s ease-out;position:relative;}" +
      ".wrap.show{opacity:1;}" +
      // 合計行 (左: 出荷予定日 / 右: 合計ラベル + 数字を右寄せ密着)
      // ラベル類 (合計/出荷予定) は分割払い/月々 と同色 (#374151)、同サイズ (12.5px) に統一
      ".total{display:flex;justify-content:space-between;align-items:baseline;gap:8px;font-size:12.5px;color:#374151;white-space:nowrap;}" +
      ".total .delivery-m{display:flex;align-items:baseline;gap:4px;color:#374151;font-size:12.5px;font-weight:600;line-height:1.2;min-width:0;flex-shrink:1;overflow:hidden;text-overflow:ellipsis;}" +
      ".total .delivery-m span{overflow:hidden;text-overflow:ellipsis;}" +
      ".total .delivery-m svg{width:14px;height:14px;flex-shrink:0;color:" + brandColor + ";align-self:center;}" +
      ".total .total-grp{display:flex;align-items:baseline;gap:4px;margin-left:auto;flex-shrink:0;}" +
      ".total b{font-size:22px;color:#111;font-weight:800;letter-spacing:-0.02em;}" +
      // クーポン適用時の行。合計を打ち消し線にし、クーポン価格を赤字で右に並べる
      ".total.has-coupon b{font-size:15px;color:#6b7280;font-weight:700;text-decoration:line-through;text-decoration-thickness:1.5px;}" +
      ".coupon{display:flex;justify-content:flex-end;align-items:baseline;gap:6px;margin-top:2px;white-space:nowrap;}" +
      ".coupon[hidden]{display:none;}" +
      ".coupon .lbl{font-size:12.5px;font-weight:700;color:#e13124;}" +
      ".coupon b{font-size:22px;font-weight:800;color:#e13124;letter-spacing:-0.02em;}" +
      // 分割払い行: 回数 select + 月々金額 (右寄せ)
      ".inst-row{display:flex;align-items:center;gap:8px;margin-top:6px;font-size:13.5px;color:#374151;flex-wrap:wrap;}" +
      ".inst-row .label{color:#374151;font-size:13.5px;}" +
      ".inst-row .monthly-inline{margin-left:auto;display:flex;align-items:baseline;gap:6px;}" +
      ".inst-row .monthly-inline .lbl{font-size:13.5px;color:#374151;}" +
      // 月々金額のアクセント色 (ブランド色とは独立、価格訴求として常に赤)
      ".inst-row .monthly-inline .val{color:#e13124;font-weight:800;font-size:22px;letter-spacing:-0.01em;}" +
      // 支払総額 / 手数料の内訳
      ".breakdown{display:flex;justify-content:space-between;gap:10px;font-size:11.5px;color:#6b7280;margin-top:3px;letter-spacing:-0.01em;}" +
      ".breakdown b{color:#374151;font-weight:600;}" +
      // カスタムドロップダウンボタン
      ".sel-btn{appearance:none;border:1px solid #d1d5db;background:#fff;border-radius:8px;padding:7px 30px 7px 14px;font-size:18px;font-weight:700;color:#111;cursor:pointer;position:relative;min-width:64px;text-align:center;font-family:inherit;}" +
      ".sel-btn::after{content:'▾';position:absolute;right:8px;top:50%;transform:translateY(-50%);font-size:12px;color:#6b7280;}" +
      ".sel-btn:active{background:#f9fafb;}" +
      // ドロップダウンパネル (popup 上向き)
      ".sel-pop{position:absolute;bottom:calc(100% + 6px);left:0;background:#fff;border:1px solid #d1d5db;border-radius:10px;box-shadow:0 8px 24px rgba(0,0,0,0.18);padding:4px;min-width:80px;max-height:240px;overflow-y:auto;-webkit-overflow-scrolling:touch;}" +
      ".sel-pop[hidden]{display:none;}" +
      ".sel-pop button{display:block;width:100%;appearance:none;border:0;background:#fff;font-size:17px;font-weight:600;color:#111;padding:10px 14px;text-align:center;cursor:pointer;border-radius:6px;font-family:inherit;}" +
      ".sel-pop button:hover{background:#f3f4f6;}" +
      ".sel-pop button.is-active{background:" + brandColor + ";color:#fff;}" +
      // CTA: shop の brand_color と cta_radius を反映
      ".cta{appearance:none;border:0;background:" + brandColor + ";color:#fff;font-weight:800;font-size:17px;padding:12px 16px;border-radius:" + ctaRadius + "px;cursor:pointer;text-decoration:none;display:block;width:100%;margin-top:10px;text-align:center;letter-spacing:0.02em;}" +
      ".cta:active{transform:scale(0.99);}" +
      ".cta[disabled]{background:#9ca3af;cursor:not-allowed;}" +
      // 注釈は必ず1行に収める。font-size を絞り、white-space:nowrap、はみ出しは
      // overflow:hidden で隠す。実機テスト時に切れる場合は文言短縮を検討。
      ".note{margin-top:4px;margin-bottom:0;font-size:10.5px;color:#9ca3af;line-height:1.3;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;letter-spacing:-0.01em;}" +
      // 快適度ゲージ (オプション機能、config.comfort_gauge.enabled = true で有効)
      // sticky-footer 内に統合: 同じ shadow DOM / 同じ wrap で配置・色を完全に揃える
      ".cg-row{display:none;align-items:center;gap:10px;padding:0 0 7px;margin-bottom:5px;border-bottom:1px solid #f3f4f6;}" +
      ".cg-row.on{display:flex;}" +
      ".cg-lbl{font-size:10px;color:#6b7280;font-weight:600;letter-spacing:0.1em;flex-shrink:0;}" +
      ".cg-num{font-size:18px;font-weight:700;color:#111;line-height:1;font-variant-numeric:tabular-nums;letter-spacing:-0.02em;min-width:24px;}" +
      ".cg-barwrap{flex:1;min-width:80px;}" +
      ".cg-bar{height:4px;background:rgba(0,0,0,0.07);border-radius:2px;overflow:hidden;}" +
      ".cg-fill{height:100%;background:#16a34a;border-radius:2px;width:50%;transition:width 0.75s cubic-bezier(0.16,1,0.3,1);}" +
      "</style>" +
      "<div class='wrap' id='wrap'>" +
        "<div class='cg-row' id='cg-row' role='progressbar' aria-valuemin='0' aria-valuemax='100'>" +
          "<span class='cg-lbl'>快適度</span>" +
          "<span class='cg-num' id='cg-num' aria-live='polite'>50</span>" +
          "<div class='cg-barwrap'><div class='cg-bar'><div class='cg-fill' id='cg-fill'></div></div></div>" +
        "</div>" +
        "<div class='total'>" +
          "<span class='delivery-m'>" +
            "<svg viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='M3 7h11v10H3z'/><path d='M14 10h4l3 3v4h-7'/><circle cx='7' cy='17' r='2'/><circle cx='17' cy='17' r='2'/></svg>" +
            "<span id='delivery-m'>—</span>" +
          "</span>" +
          "<span class='total-grp'><span>合計 (税込)</span><b id='total'>—</b></span>" +
        "</div>" +
        "<div class='coupon' id='coupon-row' hidden>" +
          "<span class='lbl' id='coupon-label'></span><b id='coupon-total'>—</b>" +
        "</div>" +
        "<div class='inst-row'>" +
          "<span class='label'>分割払い</span>" +
          "<div class='sel-host' style='position:relative;'>" +
            "<button type='button' class='sel-btn' id='count-btn'>—</button>" +
            "<div class='sel-pop' id='count-pop' hidden></div>" +
          "</div>" +
          "<span class='label'>回</span>" +
          "<span class='monthly-inline'>" +
            "<span class='lbl'>月々</span>" +
            "<span class='val' id='monthly'>—</span>" +
          "</span>" +
        "</div>" +
        "<button type='button' class='cta' id='cta'>" + (config.cta_text || defaultCtaText) + "</button>" +
        "<p class='note' id='note'></p>" +
      "</div>";
    return { host: host, shadow: shadow };
  }

  // 現在の variant_id を判定 (URL クエリ > theme の <select name="id"> > products.json の利用可能な最初)
  function detectCurrentVariantId(product) {
    var fromQuery = getQueryParam("variant");
    if (fromQuery) return String(fromQuery);
    var sel = document.querySelector('form[action*="/cart/add"] select[name="id"], form[action*="/cart/add"] input[name="id"]');
    if (sel && sel.value) return String(sel.value);
    if (product && product.variants) {
      var available = product.variants.find ? product.variants.find(function (v) { return v.available; }) : null;
      if (available) return String(available.id);
      if (product.variants[0]) return String(product.variants[0].id);
    }
    return null;
  }

  function findVariant(product, variantId) {
    if (!product || !product.variants) return null;
    for (var i = 0; i < product.variants.length; i++) {
      if (String(product.variants[i].id) === String(variantId)) return product.variants[i];
    }
    return null;
  }

  function dbg() {
    if (!window.__mdlDebug) return;
    try { console.log.apply(console, ["[MDL StickyFooter]"].concat([].slice.call(arguments))); } catch (e) {}
  }

  function init(args) {
    args = args || {};
    var config = Object.assign({}, DEFAULT_CONFIG, args.config || {});
    var mode = detectMode(config.mode);
    dbg("init called", { mode: mode, config: config, ua: navigator.userAgent, isMobile: isMobile(), innerWidth: window.innerWidth, pathname: location.pathname });

    // init 到達ログ ── 「server eligibility 通過 + module load + init 呼ばれた」 device を計測
    // shown 数 / init_called 数 で「mount で abort してる比率」 を直接見られる

    if (!isMobile() && !config.desktop_visible) {
      dbg("ABORT: not mobile and desktop_visible=false");
      return;
    }
    if (document.getElementById("mdl-sticky-footer-host")) { dbg("already mounted, skip"); return; }
    if (mode === "pdp" && !extractProductHandle()) {
      dbg("ABORT: pdp mode だが /products/<handle> URL ではない", location.pathname);
      return;
    }
    if (mode === "cart" && location.pathname.indexOf("/cart") === -1) {
      dbg("ABORT: cart mode だが /cart URL ではない", location.pathname);
      return;
    }
    dbg("gates passed, mounting Shadow DOM");

    var defaultCtaText = mode === "pdp" ? "カートに追加" : "レジに進む";
    var shopConfig = args.shopConfig || {};
    var BRAND_COLOR = shopConfig.brand_color || "#e13124";
    var CTA_RADIUS = shopConfig.cta_radius != null ? shopConfig.cta_radius : 10;
    var ui = buildShadow(config, defaultCtaText, BRAND_COLOR, CTA_RADIUS);
    var shadow = ui.shadow;
    var wrap = shadow.getElementById("wrap");
    var totalEl = shadow.getElementById("total");
    var deliveryMEl = shadow.getElementById("delivery-m");
    var monthlyEl = shadow.getElementById("monthly");
    var noteEl = shadow.getElementById("note");
    if (noteEl) noteEl.textContent = config.note_text || "";

    // ===== 快適度ゲージ (オプション機能、config.comfort_gauge.enabled = true で有効) =====
    // PDP のみ対象、Globo オプション選択を listen して数値更新。default OFF。


    // 出荷予定日: pdp-delivery-flow と同じ営業日数ロジックに統一。
    //   1. theme が data-mdl-delivery-business-days="N" 属性で埋め込んだ値 (商品メタ
    //      オブジェクト経由を想定、 商品ごとに変えられる)
    //   2. variant.config.default_business_days (admin 設定)
    //   3. デフォルト 10
    // 表示: 「最短 N 営業日でお届け予定」 (= 旧 .mdl-delivery-est--pdp テキスト依存を廃止)
    var DEFAULT_BUSINESS_DAYS = parseInt(config.default_business_days, 10);
    if (!isFinite(DEFAULT_BUSINESS_DAYS) || DEFAULT_BUSINESS_DAYS <= 0) DEFAULT_BUSINESS_DAYS = 10;
    function getBusinessDays() {
      var el = document.querySelector('[data-mdl-delivery-business-days]');
      if (el) {
        var raw = el.getAttribute('data-mdl-delivery-business-days') || el.textContent || '';
        var n = parseInt(raw, 10);
        if (isFinite(n) && n > 0) return n;
      }
      return DEFAULT_BUSINESS_DAYS;
    }
    function refreshDeliveryM() {
      if (!deliveryMEl) return;
      var days = getBusinessDays();
      deliveryMEl.parentElement.style.display = "";
      // モバイル幅で 1 行に収めるため短く: 「最短 N 営業日でお届け」 (「予定」 を省略)
      deliveryMEl.textContent = "最短 " + days + "営業日でお届け";
    }
    refreshDeliveryM();
    // メタオブジェクト値が後から DOM に挿入される theme もあるため、 body の childList を
    // 一定期間監視して値が更新されたら再描画
    (function () {
      try {
        var mo = new MutationObserver(refreshDeliveryM);
        mo.observe(document.body, { childList: true, subtree: true, attributes: true,
                                    attributeFilter: ['data-mdl-delivery-business-days'] });
        setTimeout(function(){ try { mo.disconnect(); } catch (e) {} }, 10000); // 10 秒で停止
      } catch (e) {}
    })();
    var countBtn = shadow.getElementById("count-btn");
    var countPop = shadow.getElementById("count-pop");
    // 互換性のために countEl 風オブジェクトを作る (.value getter / .addEventListener("change"))
    var _selectedCount = String(config.default_count || 36);
    var _changeHandlers = [];
    var countEl = {
      get value() { return _selectedCount; },
      set value(v) { _selectedCount = String(v); countBtn.textContent = String(v); },
      addEventListener: function (ev, fn) { if (ev === "change") _changeHandlers.push(fn); }
    };
    function setSelectedCount(v) {
      _selectedCount = String(v);
      countBtn.textContent = String(v);
      // 既存のハンドラを発火
      _changeHandlers.forEach(function (fn) { try { fn({ target: { value: _selectedCount } }); } catch (e) {} });
    }
    var ctaEl = shadow.getElementById("cta");

    var counts = (config.installment_counts || DEFAULT_CONFIG.installment_counts)
      .map(function (c) { return parseInt(c, 10); })
      .filter(function (c) { return isFinite(c) && c > 0; });
    if (!counts.length) counts = DEFAULT_CONFIG.installment_counts.slice();
    if (counts.indexOf(parseInt(_selectedCount, 10)) === -1) _selectedCount = String(counts[0]);
    // ドロップダウンのオプションをカスタム popup に描画
    function renderPopupOptions() {
      countPop.innerHTML = counts.map(function (n) {
        var active = String(n) === _selectedCount ? " is-active" : "";
        return "<button type='button' data-val='" + n + "' class='" + active.trim() + "'>" + n + "</button>";
      }).join("");
      Array.prototype.forEach.call(countPop.querySelectorAll("button"), function (b) {
        b.addEventListener("click", function () {
          setSelectedCount(b.dataset.val);
          countPop.hidden = true;
          renderPopupOptions(); // is-active 更新
        });
      });
    }
    countEl.value = _selectedCount;
    countBtn.textContent = _selectedCount;
    renderPopupOptions();
    // ボタンクリックで popup 開閉
    countBtn.addEventListener("click", function (e) {
      e.stopPropagation();
      countPop.hidden = !countPop.hidden;
    });
    // 外部クリックで popup 閉じる
    document.addEventListener("click", function () { countPop.hidden = true; });
    countPop.addEventListener("click", function (e) { e.stopPropagation(); });

    var currentTotal = 0;
    var currentVariantId = null;
    var currentProduct = null;
    var aprEl = shadow.getElementById("apr");
    var totalPayEl = shadow.getElementById("total-pay");
    var feeEl = shadow.getElementById("fee");
    function updateMonthly() {
      var n = parseInt(countEl.value, 10) || 36;
      var fees = config.fees || DEFAULT_CONFIG.fees;
      var rate = fees[String(n)];
      if (rate == null) {
        var defined = Object.keys(fees);
        rate = defined.length ? fees[defined[0]] : 0;
      }
      if (currentTotal <= 0) {
        monthlyEl.textContent = "—";
        if (aprEl) aprEl.textContent = "—";
        if (totalPayEl) totalPayEl.textContent = "—";
        if (feeEl) feeEl.textContent = "—";
        return;
      }
      // rate は「実質年率 (APR)」として解釈。
      // 月額 = amortization、支払総額 = 月額 × 回数、手数料 = 総額 - 元金。
      // これで回数が増えるほど手数料・支払総額も増える (自然)。
      var monthly = calcMonthlyByApr(currentTotal, n, rate);
      var totalPay = monthly * n;
      var fee = totalPay - currentTotal;
      monthlyEl.textContent = formatJpy(monthly);
      if (aprEl) aprEl.textContent = (rate * 100).toFixed(2);
      if (totalPayEl) totalPayEl.textContent = formatJpy(totalPay);
      if (feeEl) feeEl.textContent = formatJpy(fee);
    }
    var couponOff = parseInt(config.coupon_discount, 10) || 0;
    var couponRow = shadow.getElementById("coupon-row");
    var couponTotalEl = shadow.getElementById("coupon-total");
    var couponLabelEl = shadow.getElementById("coupon-label");
    var totalRow = shadow.querySelector(".total");
    if (couponOff > 0 && couponLabelEl) couponLabelEl.textContent = config.coupon_label || "";

    function setTotal(amount) {
      currentTotal = amount || 0;
      totalEl.textContent = currentTotal > 0 ? formatJpy(currentTotal) : "—";
      var showCoupon = couponOff > 0 && currentTotal > couponOff;
      if (couponRow) couponRow.hidden = !showCoupon;
      if (totalRow) totalRow.classList.toggle("has-coupon", showCoupon);
      if (showCoupon && couponTotalEl) couponTotalEl.textContent = formatJpy(currentTotal - couponOff);
      updateMonthly();
    }

    countEl.addEventListener("change", function () {
      updateMonthly();
    });

    // PDP 上の実「カートに追加」ボタンを探す。Globo Product Options 等は
    // form submit イベントを interceptors してオプションを動的 attach する実装が多いので、
    // 自前で fetch するより本物のボタンを click() させる方が確実。
    function findProductForm() {
      var forms = document.querySelectorAll('form[action*="/cart/add"]');
      for (var i = 0; i < forms.length; i++) {
        var f = forms[i];
        var idInput = f.querySelector('input[name="id"], select[name="id"]');
        if (idInput && idInput.value) return f;
      }
      return null;
    }
    function findRealAddToCartButton(form) {
      // Globo Product Options が挿入する clone ボタンを最優先。
      // Globo はオリジナルの "name=add" ボタンを hide し、clone を表に出して
      // clone の click を intercept してオプションを attach する。
      var globoClone = document.querySelector('.gpo-clone-atc-button:not([disabled])');
      if (globoClone && !globoClone.dataset.mdlSkip) return globoClone;

      if (form) {
        var btn = form.querySelector('button[type="submit"][name="add"]:not([disabled]), button[type="submit"]:not([disabled]), [type="submit"]:not([disabled])');
        if (btn && !btn.dataset.mdlSkip) return btn;
      }
      var any = document.querySelector('[name="add"]:not([disabled])');
      if (any && !any.dataset.mdlSkip) return any;
      return null;
    }

    function ctaAction() {
      var action = config.cta_action || "auto";
      if (action === "auto") action = mode === "pdp" ? "add_to_cart" : "checkout";
      if (action === "checkout") { location.href = "/checkout"; return; }
      if (action === "view_cart") { location.href = "/cart"; return; }
      // add_to_cart / buy_now: 現在 variant をカートに追加
      if (!currentVariantId) { return; }
      ctaEl.disabled = true;
      ctaEl.textContent = "追加中…";

      var productForm = findProductForm();
      var realBtn = findRealAddToCartButton(productForm);

      // PRIMARY: 実ボタンを click。Globo の click handler / submit interceptor が走り、
      // オプション (line item properties) が正しく attach される。
      if (mode === "pdp" && realBtn) {
        dbg("ctaAction: clicking real add-to-cart button", realBtn.className);
        try {
          // Trusted event を模倣するため、より完全な event sequence を発火。
          // 単純な click() で動かないテーマ (Globo が pointerdown 等を hook してる場合)
          // のためのフォールバック。
          realBtn.click();
          // 念のため synthetic event も dispatch (Globo が listener を click で
          // attach してれば click() で十分だが、保険として)
          var evt = new MouseEvent("click", { bubbles: true, cancelable: true, view: window });
          // realBtn.click() で既に発火してるので追加 dispatch はスキップ
          //   (二重発火で「カートに 2 件入る」事故を防止)
        } catch (e) { dbg("realBtn.click() threw", e); }
        setTimeout(function () {
          ctaEl.disabled = false;
          ctaEl.textContent = config.cta_text || defaultCtaText;
        }, 2000);
        return;
      }

      // FALLBACK: cart mode 等で実ボタンが無い場合は商品フォーム serialize → AJAX。
      // 更にフォームも無い場合は素の JSON で variant_id だけ。
      var fetchOpts;
      if (productForm) {
        var fd = new FormData(productForm);
        fd.set("quantity", "1");
        fetchOpts = {
          method: "POST",
          credentials: "same-origin",
          headers: { Accept: "application/json" },
          body: fd
        };
        dbg("ctaAction: fallback to form FormData");
      } else {
        fetchOpts = {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items: [{ id: Number(currentVariantId), quantity: 1 }] })
        };
        dbg("ctaAction: fallback to JSON (no form)");
      }

      fetch("/cart/add.js", fetchOpts)
        .then(function (r) { return r.ok ? r.json() : r.json().then(function (e) { throw e; }); })
        .then(function (added) {
          ctaEl.disabled = false;
          ctaEl.textContent = config.cta_text || defaultCtaText;
          if (action === "buy_now") location.href = "/checkout";
          else location.href = "/cart";
        })
        .catch(function (err) {
          ctaEl.disabled = false;
          ctaEl.textContent = config.cta_text || defaultCtaText;
          dbg("ctaAction: cart add failed", err);
          if (err && err.description) {
            try { alert(err.description); } catch (e) {}
          }
        });
    }
    ctaEl.addEventListener("click", function (e) {
      e.preventDefault();
      ctaAction();
    });

    // モード別ロジック
    if (mode === "pdp") {
      var handle = extractProductHandle();
      dbg("pdp mode: product handle =", handle);

      // Globo Product Options 価格要素を探す。
      // 想定 DOM: <span data-gpo-product-unit-price="" data-amount="183,800">¥203,800</span>
      // - innerText  : 現在の表示価格 (variant + Globo オプション含む合計)
      // - data-amount: base 価格
      // → innerText を優先的に読む。MutationObserver で変動を追跡。
      function findGloboPriceEl() {
        // shop config が指定する selector を最優先 (カンマ区切り対応で先頭から順に試す)
        // 例: "[data-pdp-total], .price-item--regular" → テストテーマもライブテーマも 1 設定で両対応
        if (shopConfig.price_selector) {
          var selectors = String(shopConfig.price_selector).split(',')
            .map(function (s) { return s.trim(); }).filter(Boolean);
          for (var i = 0; i < selectors.length; i++) {
            try { var custom = document.querySelector(selectors[i]); if (custom) return custom; } catch (e) {}
          }
        }
        // Globo の専用マーカー属性
        var el = document.querySelector('[data-gpo-product-unit-price]');
        if (el) return el;
        // フォールバック: 一般的な Shopify テーマの sale price 要素
        return document.querySelector('.price-item--sale.price--final') ||
               document.querySelector('.price__current .price-item--sale') ||
               document.querySelector('.price-item--regular') ||
               document.querySelector('[data-pdp-total]') ||
               document.querySelector('[data-product-price]');
      }
      function parsePriceText(text) {
        var digits = String(text || "").replace(/[^\d]/g, "");
        return digits ? parseInt(digits, 10) : 0;
      }
      var globoPriceEl = null;
      function refreshFromGlobo() {
        if (!globoPriceEl) return false;
        var p = parsePriceText(globoPriceEl.textContent);
        if (p > 0) {
          dbg("price from Globo:", p);
          setTotal(p);
          return true;
        }
        return false;
      }
      function setupGloboWatcher() {
        globoPriceEl = findGloboPriceEl();
        if (!globoPriceEl) {
          dbg("Globo price element not found yet, will retry");
          return false;
        }
        dbg("Globo price element found:", globoPriceEl.outerHTML.slice(0, 200));
        refreshFromGlobo();
        try {
          var mo = new MutationObserver(function () { refreshFromGlobo(); });
          mo.observe(globoPriceEl, {
            childList: true,
            characterData: true,
            subtree: true,
            attributes: true,
            attributeFilter: ["data-amount"]
          });
        } catch (e) { dbg("MutationObserver setup failed:", e); }
        return true;
      }
      // 初回試行 + 見つからなければ 500ms ごとに 10 回まで再試行 (Globo 遅延 load 対策)
      if (!setupGloboWatcher()) {
        var tries = 0;
        var retry = setInterval(function () {
          tries++;
          if (setupGloboWatcher() || tries >= 10) clearInterval(retry);
        }, 500);
      }

      function loadAndApply() {
        return fetchJson("/products/" + handle + ".js").then(function (product) {
          dbg("/products/<handle>.js loaded:", product ? "ok" : "null");
          if (!product) return;
          currentProduct = product;
          currentVariantId = detectCurrentVariantId(product);
          var v = findVariant(product, currentVariantId);
          dbg("current variant", { id: currentVariantId, price_cents: v && v.price });
          // Globo 要素から読めなかった時だけ variant.price をフォールバックで適用
          if (!refreshFromGlobo() && v) setTotal((v.price || 0) / 100);
        });
      }
      loadAndApply();

      // variant 変更検知: URL ?variant= の変化を interval で監視
      var lastVariantQuery = getQueryParam("variant");
      setInterval(function () {
        var cur = getQueryParam("variant");
        if (cur !== lastVariantQuery) {
          lastVariantQuery = cur;
          currentVariantId = detectCurrentVariantId(currentProduct);
          if (!refreshFromGlobo()) {
            var v = findVariant(currentProduct, currentVariantId);
            if (v) setTotal((v.price || 0) / 100);
          }
        }
      }, 500);

      // theme の <select name="id"> の change も拾う
      document.addEventListener("change", function (e) {
        var t = e.target;
        if (!t) return;
        if (t.name === "id" && (t.tagName === "SELECT" || t.type === "radio" || t.type === "hidden")) {
          currentVariantId = String(t.value);
          if (!refreshFromGlobo()) {
            var v = findVariant(currentProduct, currentVariantId);
            if (v) setTotal((v.price || 0) / 100);
          }
        }
      }, true);
    } else if (mode === "cart") {
      fetchJson("/cart.js").then(function (cart) {
        if (!cart) return;
        setTotal((cart.total_price || 0) / 100);
      });
      document.addEventListener("mdl-mkt-cart-add", function () {
        fetchJson("/cart.js").then(function (cart) { if (cart) setTotal((cart.total_price || 0) / 100); });
      });
      document.addEventListener("cart:updated", function () {
        fetchJson("/cart.js").then(function (cart) { if (cart) setTotal((cart.total_price || 0) / 100); });
      });
      // 数量変更・削除は assets/cart.js が pubsub で通知する（DOM イベントではない）
      try {
        if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS === "object") {
          subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
            fetchJson("/cart.js").then(function (cart) { if (cart) setTotal((cart.total_price || 0) / 100); });
          });
        }
      } catch (e) {}
    }

    // 同居ウィジェット (チャットボタン等) を非表示にする処理
    // hide_selectors: CSS セレクタ
    // hide_texts: 指定文字を含む要素の fixed/absolute な祖先を非表示
    function hideCohabitingWidgets() {
      var sels = config.hide_selectors || [];
      var texts = config.hide_texts || [];
      // 1. CSS セレクタ指定分
      sels.forEach(function (sel) {
        try {
          document.querySelectorAll(sel).forEach(function (el) {
            el.dataset.mdlOrigDisplay = el.style.display;
            el.style.setProperty("display", "none", "important");
          });
        } catch (e) {}
      });
      // 2. テキスト一致: 全要素から指定文字含むものを探し、fixed/absolute 祖先を隠す
      if (texts.length > 0) {
        var matches = [];
        var all = document.querySelectorAll("body *");
        for (var i = 0; i < all.length; i++) {
          var el = all[i];
          if (el.children.length > 5) continue; // 大きすぎる要素はスキップ
          var t = el.textContent || "";
          if (t.length > 60) continue; // ボタンっぽい短いテキストだけ対象
          for (var j = 0; j < texts.length; j++) {
            if (t.indexOf(texts[j]) !== -1) { matches.push(el); break; }
          }
        }
        matches.forEach(function (el) {
          // fixed / absolute な祖先を辿って消す
          var cur = el;
          for (var hops = 0; hops < 8 && cur; hops++) {
            var s = getComputedStyle(cur);
            if (s.position === "fixed" || s.position === "absolute") {
              cur.dataset.mdlOrigDisplay = cur.style.display;
              cur.style.setProperty("display", "none", "important");
              dbg("hidden cohabiting widget:", cur);
              return;
            }
            cur = cur.parentElement;
          }
        });
      }
    }
    function restoreCohabitingWidgets() {
      document.querySelectorAll("[data-mdl-orig-display]").forEach(function (el) {
        var orig = el.dataset.mdlOrigDisplay;
        if (orig) el.style.display = orig;
        else el.style.removeProperty("display");
        delete el.dataset.mdlOrigDisplay;
      });
    }

    // スクロール % で表示
    var threshold = Math.max(0, Math.min(100, config.scroll_threshold_pct || 0));
    var shown = false;
    function show() {
      if (shown) return;
      shown = true;
      wrap.classList.add("show");
      hideCohabitingWidgets();
      dbg("SHOWN");
    }
    dbg("scroll threshold =", threshold);
    if (threshold === 0) {
      setTimeout(show, 150);
    } else {
      // mount 時点で既に閾値を超えている場合に即発火する (= tracker.js / module の load 中に
      // ユーザーが先行 scroll していると scroll イベントが届かないため永遠に発火しない bug を回避)
      function checkScrollNow() {
        if (shown) return;
        var max = document.documentElement.scrollHeight - window.innerHeight;
        if (max <= 0) { show(); return; }
        var pct = (window.scrollY / max) * 100;
        if (pct >= threshold) show();
      }
      setTimeout(checkScrollNow, 100);
      window.addEventListener("scroll", checkScrollNow, { passive: true });
    }
  }

  // =====================================================================
  // 快適度ゲージ (sticky-footer 内オプション機能)
  //   - config.comfort_gauge.enabled = true で有効化 (default OFF)
  //   - 設定: { enabled, slot_selector (default "#product-option") }
  //   - 商品 handle ベースの base + 重みマップは /comfort-gauge/config API から取得
  // =====================================================================
  function initComfortGauge() {}

  function boot() {
    var cfg = window.AstroStickyFooterConfig;
    if (!cfg || !cfg.enabled) return;
    init({ config: cfg.config || {}, shopConfig: cfg.shop || {} });
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
